/**
 * HmPayments — kartoteka „Kasa i bank": wpłaty i wypłaty z Symfonii wraz z rozliczeniami.
 *
 * PUŁAPKA KONTRAKTU: na LIŚCIE Symfonia nie podaje kodu typu dokumentu (KP/KW/BP/BW) — przychodzi
 * dopiero w szczegółach. Kolumna „Typ" pokazuje więc kreskę, a grupowanie w lewym panelu idzie po
 * rodzaju operacji (wpłata/wypłata), który na liście JEST. To celowe: lepiej pogrupować po danych,
 * które mamy, niż wyświetlić puste kubełki.
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { HmPaymentListItem } from '@saas/shared';
import { Wallet } from 'lucide-react';

import type { ColumnDefinition } from '@/components/configurable-table';
import { day, HmListShell } from '@/components/handel-magazyn/HmListShell';

export function HmPayments() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDefinition<HmPaymentListItem>[]>(
    () => [
      {
        id: 'documentNumber',
        header: t('tenant.handelMagazyn.columns.documentNumber'),
        accessor: (row) => row.documentNumber,
        defaultWidth: 170,
        renderCell: (_v: unknown, row: HmPaymentListItem) => (
          <span className="font-mono text-xs text-text-primary">{row.documentNumber}</span>
        ),
      },
      {
        id: 'paymentTypeLabel',
        header: t('tenant.handelMagazyn.payments.columns.operation'),
        accessor: (row) => row.paymentTypeLabel,
        defaultWidth: 110,
      },
      {
        id: 'typeCode',
        header: t('tenant.handelMagazyn.columns.typeCode'),
        accessor: (row) => row.typeCode ?? '—',
        defaultWidth: 80,
      },
      {
        id: 'issueDate',
        header: t('tenant.handelMagazyn.columns.issueDate'),
        accessor: (row) => day(row.issueDate),
        defaultWidth: 130,
      },
      {
        id: 'registryName',
        header: t('tenant.handelMagazyn.payments.columns.registry'),
        accessor: (row) => row.registryName ?? '—',
        defaultWidth: 170,
      },
      {
        id: 'subjectName',
        header: t('tenant.handelMagazyn.payments.columns.subject'),
        accessor: (row) => row.subjectName ?? '—',
        defaultWidth: 240,
      },
      {
        id: 'totalValue',
        header: t('tenant.handelMagazyn.payments.columns.total'),
        accessor: (row) => row.totalValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 130,
        renderCell: (_v: unknown, row: HmPaymentListItem) => (
          <span className="font-medium text-text-primary">{row.totalValue}</span>
        ),
      },
      {
        id: 'settledValue',
        header: t('tenant.handelMagazyn.payments.columns.settled'),
        accessor: (row) => row.settledValue,
        dataType: 'number',
        align: 'right',
        defaultWidth: 130,
      },
      {
        id: 'settlementDate',
        header: t('tenant.handelMagazyn.payments.columns.settlementDate'),
        accessor: (row) => day(row.settlementDate),
        defaultWidth: 130,
      },
    ],
    [t],
  );

  return (
    <HmListShell<HmPaymentListItem>
      title={t('tenant.handelMagazyn.payments.title')}
      resource="payments"
      queryKey="payments"
      tableId="hm_payments"
      testIdPrefix="hm-payments"
      columns={columns}
      getRowId={(row) => String(row.id)}
      selectable
      emptyIcon={Wallet}
      groupBy={{
        label: t('tenant.handelMagazyn.payments.kinds'),
        of: (row) => row.paymentTypeLabel,
      }}
      onRowClick={(row) => navigate(`/handel-magazyn/kasa-bank/${row.id}`)}
      onNew={() => navigate('/handel-magazyn/kasa-bank/nowy')}
    />
  );
}

export default HmPayments;
